import { useHealthCheck } from '../hooks/useHealthCheck'
import { useReadinessCheck } from '../hooks/useReadinessCheck'
import { useLocale } from '../contexts/LocaleContext'

function StatusPage() {
  const { t } = useLocale()
  const { health, isLoading: healthLoading, error: healthError } = useHealthCheck()
  const { readiness, isLoading: readinessLoading, error: readinessError } = useReadinessCheck()

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-medium mb-2">{t('status.title')}</h1>
        <p className="text-[var(--text-secondary)]">
          {t('status.lead')}
        </p>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">{t('status.health')}</h2>
          {healthLoading ? (
            <span className="badge badge-info text-xs">{t('status.checking')}</span>
          ) : health ? (
            <span className="badge badge-success text-xs">{t('status.ok')}</span>
          ) : (
            <span className="badge badge-error text-xs">{t('status.failedBadge')}</span>
          )}
        </div>

        {healthError && (
          <div className="p-3 border border-[var(--accent-error)] bg-[rgba(239,68,68,0.1)] mb-4">
            <p className="text-xs text-[var(--accent-error)]">
              {healthError}
            </p>
          </div>
        )}

        {health && (
          <div className="space-y-2 text-sm">
            <div className="flex gap-4">
              <span className="text-[var(--text-tertiary)] w-32">{t('status.status')}</span>
              <span>{health.status}</span>
            </div>
            <div className="flex gap-4">
              <span className="text-[var(--text-tertiary)] w-32">{t('status.version')}</span>
              <span>{health.version}</span>
            </div>
            <div className="flex gap-4">
              <span className="text-[var(--text-tertiary)] w-32">{t('status.environment')}</span>
              <span>{health.environment}</span>
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">{t('status.readiness')}</h2>
          {readinessLoading ? (
            <span className="badge badge-info text-xs">{t('status.checking')}</span>
          ) : readiness?.ready ? (
            <span className="badge badge-success text-xs">{t('status.ready')}</span>
          ) : readiness ? (
            <span className="badge badge-error text-xs">{t('status.notReady')}</span>
          ) : (
            <span className="badge badge-error text-xs">{t('status.failedBadge')}</span>
          )}
        </div>

        {readinessError && (
          <div className="p-3 border border-[var(--accent-error)] bg-[rgba(239,68,68,0.1)] mb-4">
            <p className="text-xs text-[var(--accent-error)]">
              {readinessError}
            </p>
          </div>
        )}

        {readiness && (
          <div className="space-y-4">
            <div className="space-y-2 text-sm">
              <div className="flex gap-4">
                <span className="text-[var(--text-tertiary)] w-32">{t('status.overallReady')}</span>
                <span className={readiness.ready ? 'text-[var(--accent-success)]' : 'text-[var(--accent-error)]'}>
                  {readiness.ready ? t('status.yes') : t('status.no')}
                </span>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-3">{t('status.componentChecks')}</h3>
              <div className="space-y-2">
                {Object.entries(readiness.checks).map(([component, checkStatus]) => (
                  <div key={component} className="flex items-center justify-between p-2 bg-[var(--bg-tertiary)] border border-[var(--border-subtle)]">
                    <span className="text-sm">{component}</span>
                    <span className={`badge text-xs ${
                      checkStatus === 'ok' ? 'badge-success' :
                      checkStatus === 'not_implemented' ? 'badge-info' :
                      'badge-error'
                    }`}>
                      {checkStatus}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ background: 'var(--bg-tertiary)' }}>
        <h2 className="text-lg font-medium mb-3">{t('status.apiEndpoints')}</h2>
        <div className="space-y-2 text-xs font-mono">
          <div className="flex gap-4">
            <span className="text-[var(--text-tertiary)] w-20">GET</span>
            <code>/health</code>
            <span className="text-[var(--text-secondary)]">{t('status.healthHint')}</span>
          </div>
          <div className="flex gap-4">
            <span className="text-[var(--text-tertiary)] w-20">GET</span>
            <code>/api/v1/readyz</code>
            <span className="text-[var(--text-secondary)]">{t('status.readyHint')}</span>
          </div>
          <div className="flex gap-4">
            <span className="text-[var(--text-tertiary)] w-20">GET</span>
            <code>/api/v1/version</code>
            <span className="text-[var(--text-secondary)]">{t('status.versionHint')}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StatusPage
